// Scrollable product category column. Houses a list of ProductCheckboxRows.
// Shows a "Scroll for more ↓" affordance chip when the list overflows and
// the user hasn't reached the bottom yet.
import { useRef } from 'react';
import { Box, Typography } from '@mui/material';
import ProductCheckboxRow from './ProductCheckboxRow.jsx';
import ScrollAffordanceChip from './ScrollAffordanceChip.jsx';
import useOverflow from '../../hooks/useOverflow.js';
import scrollByPage from '../../utils/scroll-by-page.js';

const ProductColumn = ({
  category, products, options, isMobile = false,
}) => {
  const scrollRef = useRef(null);
  const categoryProducts = products.filter((p) => p.category_id === category.id);
  const { canDown } = useOverflow(scrollRef, [categoryProducts.length]);

  const scrollPage = () => scrollByPage(scrollRef.current, {
    top: (scrollRef.current?.clientHeight ?? 0) * 0.8,
  });

  const itemCount = categoryProducts.length;
  const visibleCount = scrollRef.current
    ? Math.floor(scrollRef.current.clientHeight / 72) // rough estimate per row
    : itemCount;

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0,
      position: 'relative',
      // Desktop: fill the grid cell so the list scrolls inside the column.
      // Mobile: let the column grow naturally and scroll with the page.
      ...(isMobile ? {} : { height: '100%', minHeight: 0 }),
    }}
    >
      <Typography
        variant="overline"
        color="text.secondary"
        id={`col-heading-${category.id}`}
        sx={{ px: 0.5, pb: 1.25, lineHeight: 1.4 }}
      >
        {category.name}
      </Typography>
      {category.description && (
        <Typography variant="caption" color="text.secondary" sx={{ px: 0.5, pb: 1.25, mt: -0.75 }}>
          {category.description}
        </Typography>
      )}
      <Box
        ref={scrollRef}
        role="group"
        aria-labelledby={`col-heading-${category.id}`}
        aria-describedby={`col-a11y-${category.id}`}
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          px: 0.5,
          pt: 0.25,
          pb: 0.5,
          // Product rows use overflow:hidden, which makes their flex min-height
          // resolve to 0 — without this they'd shrink to fit and never overflow,
          // so the column could never scroll. Keep them at natural height.
          '& > *': { flexShrink: 0 },
          // Desktop: independent vertical scroll, contained so it doesn't
          // chain to the page; horizontal gestures pass through to the grid.
          // Mobile: no inner scroll — the whole page scrolls instead.
          ...(isMobile
            ? { overflow: 'visible' }
            : {
              overflowY: 'auto',
              overflowX: 'hidden',
              overscrollBehaviorY: 'none',
              scrollbarWidth: 'thin',
            }),
        }}
      >
        {categoryProducts.map((product) => (
          <ProductCheckboxRow
            key={product.id}
            product={product}
            options={options.filter((o) => o.product_id === product.id)}
          />
        ))}

        <ScrollAffordanceChip
          direction="down"
          visible={canDown}
          label="Scroll for more"
          onClick={scrollPage}
          sx={{ position: 'sticky', bottom: 4, alignSelf: 'center' }}
        />
      </Box>
      {/* Screen-reader note about scrollability */}
      <Box
        component="span"
        id={`col-a11y-${category.id}`}
        sx={{
          position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden',
        }}
      >
        {`List is scrollable. ${Math.min(visibleCount, itemCount)} of ${itemCount} items visible.`}
      </Box>
    </Box>
  );
};

export default ProductColumn;
